import React from 'react';
import { MessageSquare, Plus, Clock, CheckCircle, AlertCircle, ShoppingBag, Sparkles } from 'lucide-react';

const statusConfig = {
  new: { label: 'Новый', color: '#f59e0b', icon: AlertCircle },
  in_progress: { label: 'В работе', color: '#3b82f6', icon: Clock },
  completed: { label: 'Выполнен', color: '#22c55e', icon: CheckCircle }
};

const typeConfig = {
  look: { label: 'Образ', icon: Sparkles },
  shopping: { label: 'Покупка', icon: ShoppingBag }
};

const RequestsTab = ({ requests, onAdd, onView, onDelete }) => {
  if (!requests || requests.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-xxl)' }}>
        <MessageSquare size={48} style={{ color: 'var(--soft-gray)', marginBottom: 'var(--space-m)' }} />
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--mid-gray)', marginBottom: 'var(--space-l)' }}>
          Нет запросов от клиента
        </p>
        <button
          onClick={onAdd}
          data-testid="add-request-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-s)',
            padding: '12px 24px',
            backgroundColor: 'var(--black)',
            color: 'var(--white)',
            border: 'none',
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            cursor: 'pointer'
          }}
        >
          <Plus size={16} />
          Добавить запрос
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-l)'
      }}>
        <h3 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.25rem',
          fontWeight: 400
        }}>
          Запросы клиента
        </h3>
        <button
          onClick={onAdd}
          data-testid="add-request-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-s)',
            padding: '10px 16px',
            backgroundColor: 'var(--black)',
            color: 'var(--white)',
            border: 'none',
            fontFamily: 'var(--font-body)',
            fontSize: '0.8125rem',
            cursor: 'pointer'
          }}
        >
          <Plus size={16} />
          Добавить запрос
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-m)' }}>
        {requests.map((request) => {
          const status = statusConfig[request.status] || statusConfig.new;
          const type = typeConfig[request.request_type] || typeConfig.look;
          const StatusIcon = status.icon;
          const TypeIcon = type.icon;

          return (
            <div
              key={request.id}
              onClick={() => onView(request)}
              data-testid={`request-card-${request.id}`}
              style={{
                backgroundColor: 'var(--white)',
                border: '1px solid var(--soft-gray)',
                padding: 'var(--space-l)',
                cursor: 'pointer',
                transition: 'all 200ms ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--black)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--soft-gray)'}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 'var(--space-m)'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-m)',
                    marginBottom: 'var(--space-s)'
                  }}>
                    {/* Type badge */}
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      backgroundColor: 'var(--cream)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem'
                    }}>
                      <TypeIcon size={12} />
                      {type.label}
                    </span>
                    
                    {/* Status badge */}
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      backgroundColor: `${status.color}15`,
                      color: status.color,
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem'
                    }}>
                      <StatusIcon size={12} />
                      {status.label}
                    </span>

                    {/* Created by badge */}
                    {request.created_by === 'stylist' && (
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: 'var(--soft-gray)',
                        color: 'var(--mid-gray)',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.75rem'
                      }}>
                        от стилиста
                      </span>
                    )}
                  </div>

                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    marginBottom: 'var(--space-s)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {request.description}
                  </p>

                  <div style={{
                    display: 'flex',
                    gap: 'var(--space-l)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    color: 'var(--light-gray)'
                  }}>
                    {request.budget && (
                      <span>Бюджет: {request.budget}</span>
                    )}
                    {request.deadline && (
                      <span>Дедлайн: {new Date(request.deadline).toLocaleDateString('ru-RU')}</span>
                    )}
                    <span>
                      {new Date(request.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>

                {/* Photos preview */}
                {request.photos && request.photos.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: '4px'
                  }}>
                    {request.photos.slice(0, 2).map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt=""
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover'
                        }}
                      />
                    ))}
                    {request.photos.length > 2 && (
                      <div style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'var(--cream)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.75rem',
                        color: 'var(--mid-gray)'
                      }}>
                        +{request.photos.length - 2}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RequestsTab;
